import { useEffect, useState } from "react";
import { FaSave, FaUndo } from "react-icons/fa";
import { BTN_PRIMARY, BTN_SECONDARY, Spinner } from "./MaintenanceUi";

// Bakım sensör kutularının tek hassasiyet kontrolü (#132): "Hassasiyet →
// eşik / sapma sınırı" 1-10 kutu satırı seçilebilir; seçilen seviye kayıtlı
// değerden farklıysa önizleme olur ve Kaydet / Vazgeç çıkar. Askı, fincan ve
// değirmen ortak kullanır (eski −/+ satırının yerine).

/**
 * Seçim taslağı + kaydetme durumu.
 * @param {number|null} saved makinedeki kayıtlı değer (bilinmiyorsa null)
 * @param {Function} [onSave] (value) => Promise<boolean>; yoksa salt-okunur
 */
export function useSensitivityDraft(saved, onSave) {
  const [draft, setDraft] = useState(null);
  // idle | saving | sent (makineye gitti, yeni değer bekleniyor)
  const [phase, setPhase] = useState("idle");

  // Makineden yeni kayıtlı değer gelince taslak kapanır.
  useEffect(() => {
    if (draft !== null && saved === draft) {
      setDraft(null);
      setPhase("idle");
    }
  }, [saved, draft]);

  const editable = typeof onSave === "function" && saved !== null;
  const pending = draft !== null && draft !== saved;

  const select = (v) => {
    if (!editable || phase === "saving") return;
    setPhase("idle");
    setDraft(v === saved ? null : v);
  };
  const cancel = () => {
    setDraft(null);
    setPhase("idle");
  };
  const save = async () => {
    if (!pending || !editable) return;
    setPhase("saving");
    let ok = false;
    try {
      ok = await onSave(draft);
    } catch (_) {
      ok = false;
    }
    setPhase(ok === false ? "idle" : "sent");
  };

  return {
    draft: pending ? draft : null,
    selected: pending ? draft : saved,
    editable,
    phase,
    select,
    cancel,
    save,
  };
}

/**
 * Hassasiyet 1-10 → eşik/sınır kutuları. `selected` vurgulu; `saved`
 * seçilenden farklıysa ince çerçeveyle işaretlenir.
 * @param {object} props
 * @param {{ sensitivity: number, threshold: number }[]} props.table
 * @param {number|null} props.selected
 * @param {number|null} [props.saved]
 * @param {Function} [props.onSelect] yoksa kutular salt-okunur
 * @param {string} [props.readOnlyTitle] salt-okunurken ipucu
 */
export function SensitivityScale({
  table,
  selected,
  saved = null,
  onSelect,
  readOnlyTitle,
}) {
  return (
    <div
      data-testid="sensitivity-scale"
      className="grid grid-cols-10 gap-0.5 text-center"
      title={onSelect ? undefined : readOnlyTitle}
    >
      {table.map((t) => {
        const active = t.sensitivity === selected;
        const isSaved =
          saved !== null && t.sensitivity === saved && saved !== selected;
        const cls = `rounded px-0.5 py-1 ${
          active ? "bg-main text-white" : "bg-gray-50 text-gray-600"
        } ${isSaved ? "ring-1 ring-inset ring-amber-500" : ""}`;
        const content = (
          <>
            <div className="text-xs font-Bold">{t.sensitivity}</div>
            <div className="text-[9px] tabular-nums opacity-80">
              {t.threshold}
            </div>
          </>
        );
        const testId = active
          ? "scale-active"
          : isSaved
            ? "scale-saved"
            : undefined;
        return onSelect ? (
          <button
            key={t.sensitivity}
            type="button"
            data-testid={testId}
            aria-pressed={active}
            aria-label={`Hassasiyet ${t.sensitivity}`}
            onClick={() => onSelect(t.sensitivity)}
            className={`${cls} ${active ? "" : "hover:bg-gray-200"} transition-colors`}
          >
            {content}
          </button>
        ) : (
          <div key={t.sensitivity} data-testid={testId} className={cls}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Başlık + seçilebilir tablo + Kaydet / Vazgeç (taslak varken).
 * @param {object} props
 * @param {string} props.title ör. "Hassasiyet → eşik"
 * @param {string} props.unit tablo değerinin adı ("eşik" / "sapma sınırı")
 * @param {{ sensitivity: number, threshold: number }[]} props.table
 * @param {number|null} props.saved kayıtlı değer
 * @param {number} props.fallback kayıtlı değer bilinmiyorsa vurgulanan (5)
 * @param {ReturnType<typeof useSensitivityDraft>} props.picker
 * @param {boolean} props.canWrite yazma fonksiyonu verildi mi
 */
export function SensitivityPicker({
  title,
  unit,
  table,
  saved,
  fallback,
  picker,
  canWrite,
}) {
  const { draft, selected, editable, phase, select, cancel, save } = picker;
  const limitOf = (s) => table.find((t) => t.sensitivity === s)?.threshold;
  const headState =
    saved === null
      ? `fabrika ${fallback}`
      : draft !== null
        ? `şu an ${saved} · seçilen ${draft}`
        : `şu an ${saved}`;
  const readOnlyTitle = !canWrite
    ? "Hassasiyeti değiştirme yetkiniz yok"
    : "Canlı değer gelince seçilebilir";

  return (
    <section data-testid="sensitivity-picker" className="flex flex-col gap-1">
      <h4 className="text-xs font-SemiBold text-gray-600">
        {title} ({headState})
      </h4>
      <SensitivityScale
        table={table}
        selected={selected ?? fallback}
        saved={saved}
        onSelect={editable ? select : undefined}
        readOnlyTitle={readOnlyTitle}
      />
      {editable && draft === null && phase === "idle" && (
        <p className="text-[10px] text-gray-400">
          Değiştirmek için bir kutuya basın; önizleme sonra Kaydet.
        </p>
      )}
      {draft !== null && (
        <div className="mt-1 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
          <span className="min-w-0 flex-1 text-xs text-amber-800">
            {phase === "sent" ? (
              <>
                {draft} gönderildi; makineden gelen bir sonraki okumada görünür.
              </>
            ) : (
              <>
                Kayıtlı {saved} ({unit} {limitOf(saved)}) → seçilen {draft} (
                {unit} {limitOf(draft)})
              </>
            )}
          </span>
          <button
            type="button"
            onClick={cancel}
            disabled={phase === "saving"}
            className={`${BTN_SECONDARY} h-8 px-2.5`}
          >
            <FaUndo className="w-3 h-3" />
            Vazgeç
          </button>
          {phase !== "sent" && (
            <button
              type="button"
              onClick={save}
              disabled={phase === "saving"}
              className={`${BTN_PRIMARY} h-8 px-3`}
            >
              {phase === "saving" ? (
                <Spinner />
              ) : (
                <FaSave className="w-3.5 h-3.5" />
              )}
              {phase === "saving" ? "Gönderiliyor" : "Kaydet"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
